/**
 * Channel detail right-panel + Guide helpers (pure).
 * Keeps Now Playing metadata aligned with currently playing media and
 * seeds Guide segments from published now/next when EPG window is empty.
 */

var guideTimeline = require("./guideTimeline");
var isSameGuideProgram = guideTimeline.isSameGuideProgram;

/**
 * Canonical ID for the media currently playing from /channels/now media_asset.
 * Prefer admin_video_id (numeric) so it can match videos/view payloads.
 * @param {object|null} mediaAsset
 * @returns {string}
 */
function currentPlayingMediaId(mediaAsset) {
  if (!mediaAsset) return "";
  var meta = mediaAsset.metadata || {};
  var candidates = [
    meta.admin_video_id,
    meta.resolved_admin_video_id,
    mediaAsset.admin_video_id,
    mediaAsset.id,
    mediaAsset.media_asset_key,
  ];
  for (var i = 0; i < candidates.length; i++) {
    var c = candidates[i];
    if (c == null || c === "") continue;
    var s = String(c);
    // Normalize "admin_video:123" → "123" for cross-source compare.
    var m = s.match(/^admin_video:(\d+)$/i);
    return m ? m[1] : s;
  }
  return "";
}

/**
 * Resolve admin_video_id from a videos/view (or similar) payload.
 * @param {object|null} airingVideo
 * @returns {string}
 */
function airingVideoMediaId(airingVideo) {
  if (!airingVideo) return "";
  var candidates = [
    airingVideo.admin_video_id,
    airingVideo.id,
    airingVideo.video_id,
  ];
  for (var i = 0; i < candidates.length; i++) {
    if (candidates[i] != null && candidates[i] !== "") {
      return String(candidates[i]);
    }
  }
  return "";
}

/**
 * True only when airingVideo refers to the same media as media_asset.
 * Prevents stale videos/view descriptions from leaking across program switches.
 * @param {object|null} airingVideo
 * @param {object|null} mediaAsset
 * @returns {boolean}
 */
function airingVideoMatchesPlayingMedia(airingVideo, mediaAsset) {
  var playing = currentPlayingMediaId(mediaAsset);
  var airing = airingVideoMediaId(airingVideo);
  if (!playing || !airing) return false;
  return playing === airing;
}

/**
 * Follow /channels/now advances unless the user picked a non-current Guide segment.
 * @param {object|null} prev
 * @param {object|null} current
 * @param {boolean} userPicked
 * @returns {{ program: object|null, clearUserPick: boolean }}
 */
function followSelectedProgram(prev, current, userPicked) {
  if (!current) {
    return { program: prev || null, clearUserPick: false };
  }
  if (!prev) {
    return { program: current, clearUserPick: false };
  }
  if (!userPicked) {
    return { program: current, clearUserPick: false };
  }
  if (isSameGuideProgram(prev, current)) {
    return { program: current, clearUserPick: true };
  }
  return { program: prev, clearUserPick: false };
}

/**
 * Title/description for the aside panel — single SSOT when viewing current airing.
 * Never mixes a non-current selection title with current media_asset short_description.
 * Never uses airingVideo description unless its media ID equals currentPlayingMediaId.
 *
 * @param {object} opts
 * @param {object} opts.displayProgram
 * @param {object|null} opts.current
 * @param {object} opts.mediaAsset
 * @param {object|null} opts.airingVideo
 * @param {string} [opts.fallbackTitle]
 * @returns {{ isCurrent: boolean, title: string, description: string, playingMediaId: string, descriptionMediaId: string }}
 */
function resolveProgramPanelMeta(opts) {
  var displayProgram = (opts && opts.displayProgram) || {};
  var current = (opts && opts.current) || null;
  var mediaAsset = (opts && opts.mediaAsset) || {};
  var airingVideo = (opts && opts.airingVideo) || null;
  var fallbackTitle = (opts && opts.fallbackTitle) || "";
  var mediaMeta = (mediaAsset && mediaAsset.metadata) || {};
  var isCurrent = !!(current && isSameGuideProgram(displayProgram, current));
  var playingId = currentPlayingMediaId(mediaAsset);
  var matchedAiring = airingVideoMatchesPlayingMedia(airingVideo, mediaAsset)
    ? airingVideo
    : null;

  if (isCurrent) {
    // SSOT: currently playing media_asset first; matched videos/view only as same-ID enrichment.
    var liveTitle =
      (mediaAsset && mediaAsset.title) ||
      (matchedAiring && matchedAiring.title) ||
      displayProgram.title ||
      fallbackTitle ||
      "";
    var liveDesc =
      (mediaAsset && mediaAsset.description) ||
      mediaMeta.short_description ||
      (matchedAiring && matchedAiring.description) ||
      displayProgram.description ||
      "";
    var descMediaId = playingId;
    if (!descMediaId && matchedAiring) {
      descMediaId = airingVideoMediaId(matchedAiring);
    }
    return {
      isCurrent: true,
      title: String(liveTitle || ""),
      description: String(liveDesc || ""),
      playingMediaId: playingId,
      descriptionMediaId: descMediaId,
    };
  }

  return {
    isCurrent: false,
    title: String(displayProgram.title || fallbackTitle || ""),
    description: String(displayProgram.description || ""),
    playingMediaId: playingId,
    descriptionMediaId: "",
  };
}

/**
 * Match a channels/epg grid row to the channel page identity.
 */
function matchEpgChannelRow(channels, channel, channelKey) {
  var ch = channel || {};
  var id = ch.id != null ? String(ch.id) : "";
  var uid = ch.unique_id ? String(ch.unique_id) : "";
  var slug = String(ch.slug || channelKey || "");
  var list = Array.isArray(channels) ? channels : [];
  for (var i = 0; i < list.length; i++) {
    var row = list[i];
    var c = (row && row.channel) || {};
    if (id && String(c.id) === id) return row;
    if (uid && String(c.unique_id) === uid) return row;
    if (slug && (String(c.slug) === slug || String(c.unique_id) === slug)) {
      return row;
    }
  }
  return null;
}

/**
 * Stable identity for Guide program merge (entry id + start).
 * @param {object|null} p
 * @returns {string}
 */
function guideProgramIdentity(p) {
  if (!p || typeof p !== "object") return "";
  var id = p.schedule_entry_id != null ? p.schedule_entry_id : p.id;
  return String(id || "") + "|" + String(p.starts_at || "");
}

/**
 * Seed Guide from published now/next when the EPG window returns no programs.
 * @param {object|null} nowPayload
 * @returns {object[]}
 */
function seedGuideProgramsFromNow(nowPayload) {
  var out = [];
  if (!nowPayload || nowPayload.success === false) return out;
  if (nowPayload.current) out.push(nowPayload.current);
  if (nowPayload.next) out.push(nowPayload.next);
  return out;
}

/**
 * Prefer EPG programs; merge now/next when the window is empty or sparse
 * (e.g. Continuous Loop occurrence lag left only "current").
 * @param {object[]} programs
 * @param {object|null} nowPayload
 * @returns {object[]}
 */
function resolveChannelGuidePrograms(programs, nowPayload) {
  var list = Array.isArray(programs) ? programs.slice() : [];
  var seeded = seedGuideProgramsFromNow(nowPayload);
  if (list.length === 0) {
    return seeded;
  }
  if (seeded.length === 0) {
    return list;
  }
  var seen = {};
  list.forEach(function (p) {
    var key = guideProgramIdentity(p);
    if (key) seen[key] = true;
  });
  seeded.forEach(function (p) {
    var key = guideProgramIdentity(p);
    if (!key || seen[key]) return;
    list.push(p);
    seen[key] = true;
  });
  list.sort(function (a, b) {
    var ta = a && a.starts_at ? Date.parse(a.starts_at) : 0;
    var tb = b && b.starts_at ? Date.parse(b.starts_at) : 0;
    if (isNaN(ta)) ta = 0;
    if (isNaN(tb)) tb = 0;
    return ta - tb;
  });
  return list;
}

/**
 * Non-empty trimmed description string, or "".
 * @param {object|null} obj
 * @returns {string}
 */
function nonEmptyDescription(obj) {
  if (!obj || obj.description == null) return "";
  var s = String(obj.description).trim();
  return s;
}

/**
 * Channel page HEADER chrome — describes the CHANNEL only.
 * Prefer /channels/now channel (scheduler SSOT), then nested content/show channel.
 * Never inherit content/show top-level description from a video (or other) ContentItem,
 * and never use current/previous program or airingVideo text.
 *
 * @param {object} opts
 * @param {object|null} opts.nowChannel  from /channels/now
 * @param {object|null} opts.detail      from content/show
 * @returns {{ channel: object, title: string, description: string, banner: *, logo: *, source: string }}
 */
function resolveChannelHeaderMeta(opts) {
  var nowChannel = (opts && opts.nowChannel) || null;
  var detail = (opts && opts.detail) || null;
  var detailIsChannel =
    !!(detail && String(detail.content_type || "").toLowerCase() === "channel");
  var detailChannel =
    detailIsChannel && detail.channel && typeof detail.channel === "object"
      ? detail.channel
      : null;

  // Prefer now-playing channel identity; fill only when now has not resolved yet.
  var channel = nowChannel || detailChannel || {};
  var title =
    (channel && channel.name) ||
    (detailIsChannel && detail.title) ||
    "Channel";
  var banner =
    (channel && channel.banner) ||
    (detailIsChannel ? detail.banner : null) ||
    null;
  var logo =
    (channel && channel.logo) ||
    (detailIsChannel ? detail.thumbnail : null) ||
    null;

  // Description: channel objects only — never video ContentItem / program text.
  var description =
    nonEmptyDescription(nowChannel) ||
    nonEmptyDescription(detailChannel) ||
    (detailIsChannel ? nonEmptyDescription(detail) : "") ||
    "";

  var source = "none";
  if (nonEmptyDescription(nowChannel)) source = "now.channel";
  else if (nonEmptyDescription(detailChannel)) source = "detail.channel";
  else if (detailIsChannel && nonEmptyDescription(detail)) source = "detail.channel_item";

  return {
    channel: channel,
    title: title,
    description: description,
    banner: banner,
    logo: logo,
    source: source,
  };
}

module.exports = {
  followSelectedProgram: followSelectedProgram,
  resolveProgramPanelMeta: resolveProgramPanelMeta,
  currentPlayingMediaId: currentPlayingMediaId,
  airingVideoMediaId: airingVideoMediaId,
  airingVideoMatchesPlayingMedia: airingVideoMatchesPlayingMedia,
  matchEpgChannelRow: matchEpgChannelRow,
  seedGuideProgramsFromNow: seedGuideProgramsFromNow,
  resolveChannelGuidePrograms: resolveChannelGuidePrograms,
  resolveChannelHeaderMeta: resolveChannelHeaderMeta,
};
